// Pure viewport/transform arithmetic for the pedigree canvas (Phase A).
//
// Kept free of d3 (and any DOM) so it is unit-testable under jest — zoom.js imports
// these and wraps the results in d3.zoomIdentity. A d3 transform maps layout coords →
// screen: screen = {x,y} + k·world.

export const SCALE_MIN = 0.05;
export const SCALE_MAX = 0.5;
// Cap the first-open zoom so a small pedigree doesn't open uncomfortably close-up;
// we'd rather show a little context around the focus than fill the screen with one node.
export const STANDARD_OPEN_SCALE = 0.3;

export const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// The scale that fits a content bbox into the viewport, clamped to the zoom extent.
// Returns null when the bbox isn't measurable (width/height ≤ 0).
export const fitScale = (vw, vh, bbox) => {
  if (!bbox || bbox.width <= 0 || bbox.height <= 0) return null;
  const margin = 0.9; // ~10% padding around the content
  return clamp(
    margin * Math.min(vw / bbox.width, vh / bbox.height),
    SCALE_MIN,
    SCALE_MAX
  );
};

// Translate that maps the bbox centre to the viewport centre at scale k.
export const fitTranslate = (vw, vh, bbox, k) => ({
  x: vw / 2 - k * (bbox.x + bbox.width / 2),
  y: vh / 2 - k * (bbox.y + bbox.height / 2)
});

// First-open scale: fit the tree, but never zoom in past STANDARD (so a small family
// isn't over-zoomed) — a big family still opens zoomed-out enough to show context.
export const openScale = (fk) =>
  fk != null ? Math.min(fk, STANDARD_OPEN_SCALE) : STANDARD_OPEN_SCALE;

// Translate that centres a world point in the viewport at scale k.
export const centerTranslate = (vw, vh, center, k) => ({
  x: vw / 2 - k * center.x,
  y: vh / 2 - k * center.y
});

// Keep a point fixed on screen while its world coords move by `delta`: translate by
// -k·delta. No-op when delta is absent/zero/non-finite.
export const reanchoredTranslate = (stored, delta) => {
  const { x, y, k } = stored;
  if (delta && Number.isFinite(delta.dx) && Number.isFinite(delta.dy)) {
    return { x: x - k * delta.dx, y: y - k * delta.dy, k };
  }
  return { x, y, k };
};

// Screen position of a world point under a transform (the invariant reanchor preserves).
export const screenPos = (transform, world) => ({
  x: transform.x + transform.k * world.x,
  y: transform.y + transform.k * world.y
});

export const isFinitePoint = (p) =>
  !!p && Number.isFinite(p.x) && Number.isFinite(p.y);
